import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  ControlTowerWrapper,
  TaskSummaryWrapper,
  TaskDetailWrapper,
  UserInfoWrapper,
  VerifyTaskWrapper,
  UserManagementWrapper,
} from './RouteWrappers';
import { useAuth } from '../../context/auth';
import LandingPage from '../landing/LandingPage';
import Login from '../auth/Login';
import TripsConsole from '../trips/TripsConsole';

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  // Log routing information for debugging
  useEffect(() => {
    console.log(`Current route: ${location.pathname}`);
    console.log(`Authentication status: ${isAuthenticated ? 'Authenticated' : 'Not Authenticated'}`);
  }, [location.pathname, isAuthenticated]);
  
  // If authentication is loading, show nothing
  if (authLoading) {
    return null;
  }

  return (
    <Routes>
      {/* Public landing page */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/platform" element={<LandingPage />} />
      {/* Deployments section was removed; keep the URL alive for previously shared links */}
      <Route path="/deployments" element={<Navigate to="/" replace />} />
      <Route path="/contact" element={<LandingPage />} />

      {/* Login route */}
      <Route 
        path="/login" 
        element={
          isAuthenticated ? 
            <Navigate to="/task-summary" replace /> : 
            <Login />
        } 
      />
      
      {/* Protected routes - require authentication */}
      {isAuthenticated ? (
        <>
          {/* Task Summary route with hub parameter */}
          <Route 
            path="/task-summary" 
            element={<Navigate to="/task-summary/hub/default" replace />} 
          />
          
          <Route 
            path="/task-summary/hub/:hubId" 
            element={<TaskSummaryWrapper />} 
          />

          <Route
            path="/control-tower"
            element={<ControlTowerWrapper />}
          />

          <Route path="/trips/hub/:hubId" element={<TripsConsole />} />
          <Route path="/trips/hub/:hubId/trip/:tripId" element={<TripsConsole />} />
          
          {/* Task Detail route with hub and task parameters */}
          <Route 
            path="/edit-task/hub/:hubId/task/:taskId" 
            element={<TaskDetailWrapper />} 
          />
          
          {/* User Info route with hub parameter */}
          <Route 
            path="/user-info/hub/:hubId" 
            element={<UserInfoWrapper />} 
          />
          
          {/* Verify Task route with hub and task parameters */}
          <Route 
            path="/verify-task/hub/:hubId/task/:taskId" 
            element={<VerifyTaskWrapper />} 
          />

          {/* User Management routes */}
          <Route 
            path="/user-management" 
            element={<Navigate to="/user-management/hub/default" replace />} 
          />
          <Route 
            path="/user-management/hub/:hubId" 
            element={<UserManagementWrapper />} 
          />
          {/* Catch-all for authenticated users */}
          <Route 
            path="*" 
            element={<Navigate to="/task-summary" replace />} 
          />
        </>
      ) : (
        // Redirect to login if not authenticated
        <Route 
          path="*" 
          element={<Navigate to="/login" replace />} 
        />
      )}
    </Routes>
  );
};

export default AppRoutes; 
